import React, { useState, useEffect } from 'react'
import { NavLink, useNavigate, Outlet } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  LayoutDashboard, 
  Users, 
  GitBranch, 
  FileText, 
  Settings, 
  LogOut, 
  Clock,
  Shield,
  ChevronLeft,
  Menu,
  ExternalLink
} from 'lucide-react'
import { logout, getSession, getTimeToExpiry } from '../utils/auth'
import { logAction, ActionTypes } from '../utils/auditLogger'

const AdminLayout = () => {
  const navigate = useNavigate()
  const [session, setSession] = useState(null)
  const [timeToExpiry, setTimeToExpiry] = useState(0)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  // Обновление информации о сессии каждую минуту
  useEffect(() => {
    let isMounted = true
    let interval = null

    const updateSessionInfo = async () => {
      if (!isMounted) return

      try {
        // Если сессии нет, останавливаем опрос
        const sessionData = await getSession()
        if (!isMounted) return

        if (!sessionData) {
          setSession(null)
          setTimeToExpiry(0)
          if (interval) clearInterval(interval) // Останавливаем опрос при потере сессии
          // Можно добавить редирект, но ProtectedRoute это сделает при навигации
          return
        }

        const timeLeft = await getTimeToExpiry()
        if (!isMounted) return

        setSession(sessionData)
        setTimeToExpiry(timeLeft)
      } catch (error) {
        console.error('Ошибка обновления информации о сессии:', error)
        if (isMounted) {
          setSession(null)
          setTimeToExpiry(0)
          if (interval) clearInterval(interval)
        }
      }
    }

    updateSessionInfo()
    interval = setInterval(updateSessionInfo, 10000) // каждые 10 секунд для демонстрации

    return () => {
      isMounted = false
      if (interval) clearInterval(interval)
    }
  }, [])

  const handleLogout = async () => {
    logAction(ActionTypes.LOGOUT, { 
      sessionDuration: session ? Date.now() - session.loginTime : 0 
    })
    await logout()
    navigate('/admin/login')
  }

  const menuItems = [
    {
      path: '/admin/dashboard',
      icon: LayoutDashboard,
      label: 'Дашборд',
      description: 'Обзор системы'
    },
    {
      path: '/admin/departments',
      icon: Users,
      label: 'Отделы',
      description: 'Управление отделами и должностями'
    },
    {
      path: '/admin/transitions',
      icon: GitBranch,
      label: 'Переходы',
      description: 'Карьерные пути'
    },
    {
      path: '/admin/audit',
      icon: FileText,
      label: 'Аудит',
      description: 'Логи изменений'
    },
    {
      path: '/admin/settings',
      icon: Settings,
      label: 'Настройки',
      description: 'Импорт/экспорт данных'
    }
  ]

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Боковая панель */}
      <motion.div
        initial={false}
        animate={{
          width: sidebarCollapsed ? 80 : 280
        }}
        transition={{ duration: 0.3 }}
        className="bg-white shadow-lg flex flex-col"
      >
        {/* Заголовок */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          {!sidebarCollapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">HR Админ</h2>
                <p className="text-xs text-gray-500">Панель управления</p>
              </div>
            </motion.div>
          )}
          
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {sidebarCollapsed ? (
              <Menu className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>

        {/* Навигация */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-primary-50 text-primary-700 border border-primary-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="font-medium">{item.label}</div>
                  <div className="text-xs opacity-75">{item.description}</div>
                </motion.div>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Информация о сессии */}
        {!sidebarCollapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-4 border-t border-gray-200 space-y-3"
          >
            {/* Пользователь */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-secondary-700">
                  {session?.user?.[0] || 'H'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {session?.user || 'HR Admin'}
                </p>
                <p className="text-xs text-gray-500">Администратор</p>
              </div>
            </div>

            {/* Время сессии */}
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>
                {timeToExpiry > 60 
                  ? `${Math.floor(timeToExpiry / 60)}ч ${timeToExpiry % 60}м` 
                  : `${timeToExpiry}м`
                } до окончания
              </span>
            </div>

            {/* Кнопка выхода */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-2 p-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Выйти</span>
            </button>
          </motion.div>
        )}

        {/* Свернутая информация о сессии */}
        {sidebarCollapsed && (
          <div className="p-4 border-t border-gray-200 space-y-3">
            <button
              onClick={handleLogout}
              className="w-full p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Выйти"
            >
              <LogOut className="w-5 h-5 mx-auto" />
            </button>
          </div>
        )}
      </motion.div>

      {/* Основной контент */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Верхняя панель */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Админ-панель Карьерной Матрицы HR
              </h1>
              <p className="text-sm text-gray-600">
                Управление организационной структурой и карьерными путями
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Индикатор времени сессии */}
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                <span>
                  {timeToExpiry < 30 ? (
                    <span className="text-red-600 font-medium">
                      {timeToExpiry} мин до завершения
                    </span>
                  ) : (
                    `${Math.floor(timeToExpiry / 60)}ч ${timeToExpiry % 60}м`
                  )}
                </span>
              </div>
              
              {/* Ссылка на публичную версию */}
              <button
                onClick={() => window.open('/hr/', '_blank')}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="hidden sm:inline">Открыть публичную версию</span>
              </button>
            </div>
          </div>
        </header>

        {/* Контент страницы */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default AdminLayout 